import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, promptImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const markSeen = (seenAt) => api.post("/drops/seen", { seen_at: seenAt }).catch(() => { /* retried on next visit */ });

const freshStyles = async (since) => {
  const { data } = await api.get("/prompts");
  return data.filter((p) => p.released_at && new Date(p.released_at) > new Date(since));
};

export const DropAlert = () => {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [styles, setStyles] = useState([]);
  const [dropAt, setDropAt] = useState(null);
  const userId = user ? user.user_id : null;
  const seen = user ? user.seen_drop_at : null;

  useEffect(() => {
    if (!userId) return;
    let live = true;
    api.get("/drops/next").then(async ({ data }) => {
      const last = data.last_drop_at;
      if (!last || (seen && new Date(last) <= new Date(seen))) return;
      const fresh = seen ? await freshStyles(seen) : [];
      if (!live) return;
      if (fresh.length) { setStyles(fresh); setDropAt(last); }
      else markSeen(last);
    }).catch(() => { /* alert is optional */ });
    return () => { live = false; };
  }, [userId, seen]);

  const close = () => {
    if (dropAt) {
      markSeen(dropAt);
      setUser((u) => (u ? { ...u, seen_drop_at: dropAt } : u));
    }
    setDropAt(null);
  };

  const go = (p) => { close(); nav(`/studio?prompt=${p.id}`); };

  return (
    <Dialog open={Boolean(dropAt)} onOpenChange={(v) => !v && close()}>
      <DialogContent className="rounded-3xl sm:max-w-xl" data-testid="drop-alert-dialog">
        <DialogHeader>
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#FF5A79] text-white px-3 py-1 text-xs font-bold uppercase tracking-wider"><Sparkles className="h-3.5 w-3.5" /> Weekly drop</span>
          <DialogTitle className="font-display text-3xl pt-2">New styles just dropped!</DialogTitle>
          <p className="text-sm text-[#6E687A]">Fresh from the parlor. Be the first to try them on your pet.</p>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {styles.slice(0, 4).map((p) => (
            <button key={p.id} onClick={() => go(p)} data-testid={`drop-alert-style-${p.id}`}
              className="group text-left rounded-2xl overflow-hidden border border-[#EAE3D9] bg-white hover:-translate-y-1 transition-transform">
              <div className="aspect-square overflow-hidden"><img src={promptImg(p)} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <p className="p-3 font-display font-semibold">{p.title}</p>
            </button>
          ))}
        </div>
        <button onClick={close} data-testid="drop-alert-close"
          className="w-full rounded-full bg-[#1E1B26] text-white py-3 font-bold hover:bg-[#FF5A79] transition-colors">Got it</button>
      </DialogContent>
    </Dialog>
  );
};
